'use client'

import { useState, Suspense } from 'react'
import Link from '@/components/i18n/link'
import { useSearchParams } from 'next/navigation'
import { useLocalizedRouter as useRouter, useMessages } from '@/lib/i18n/client'
import messages from './page.i18n'
import { Loader2 } from 'lucide-react'
import { loginUser } from '@/lib/api'
import { PasswordInput } from '@/components/password-input'
import { useAuthStore } from '@/lib/store/auth-store'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { track } from '@/lib/tracking/track'

function LoginForm() {
  const router = useRouter()
  const t = useMessages(messages)
  const searchParams = useSearchParams()
  const sessionExpired = searchParams.get('session') === 'expired'
  const redirectTo = searchParams.get('redirect')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const setUser = useAuthStore((state) => state.setUser)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      const user = await loginUser({ email, password })
      setUser(user)
      track('login', { method: 'email' })
      // Solo seguimos rutas internas (empiezan con "/" y no "//") para evitar
      // que ?redirect= se use como vector de open redirect.
      const isSafeRedirect = (path: string | null): path is string =>
        !!path && path.startsWith('/') && !path.startsWith('//')
      router.push(isSafeRedirect(redirectTo) ? redirectTo : '/dashboard')
      // No apagamos el loading aquí: el botón sigue mostrando el spinner hasta
      // que /dashboard termine de cargar y este componente se desmonte. Si lo
      // apagáramos, el usuario ve el botón "normal" otra vez sin ninguna señal
      // de que la navegación sigue en curso.
    } catch (err) {
      setError(err instanceof Error ? err.message : t.error)
      setLoading(false)
    }
  }

  return (
    <div className="rounded-2xl border border-border bg-card p-8 shadow-soft">
      <div className="mb-6 text-center">
        <h1 className="text-2xl font-semibold tracking-tight">{t.title}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t.subtitle}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t.emailPlaceholder}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="password">{t.password}</Label>
          <PasswordInput
            id="password"
            value={password}
            onChange={setPassword}
            required
          />
        </div>

        <div className="text-right">
          <Link href="/forgot-password" className="text-xs text-muted-foreground hover:text-foreground transition">
            {t.forgot}
          </Link>
        </div>

        {sessionExpired && !error && (
          <p className="rounded-lg bg-amber-50 px-3.5 py-2.5 text-sm text-amber-700 dark:bg-amber-950/30 dark:text-amber-400">
            {t.sessionExpired}
          </p>
        )}

        {error && (
          <p className="rounded-lg bg-red-50 px-3.5 py-2.5 text-sm text-red-600 dark:bg-red-950/30 dark:text-red-400">
            {error}
          </p>
        )}

        <Button
          type="submit"
          disabled={loading}
          className="w-full rounded-full"
        >
          {loading && <Loader2 className="h-4 w-4 animate-spin" />}
          {t.submit}
        </Button>
      </form>

      <p className="mt-6 text-center text-sm text-muted-foreground">
        {t.noAccount}{' '}
        <Link href="/registro" className="font-medium text-foreground hover:text-primary">
          {t.register}
        </Link>
      </p>
    </div>
  )
}

export default function LoginPage() {
  return (
    <Suspense>
      <LoginForm />
    </Suspense>
  )
}
